import { Feature } from "ol";
import { ISingleRowList } from "../Coordinates/DropdownList";
import { IFeatureGroup, Layer as GeoJSLayer, IFeature as GeoJSFeature } from "@geo/js";

export interface ILayerConfig {
    id: string;
    label: string;
    displayExpression: string;
    query: string;
    queryHelp: string;
    validationRegEx: string;
    url: string;
    serviceId: string;
    layerInfo: any; //__esri.LayerInfo;
    queryNonLocale?: string;
    type?: string;
    minResultCount: number;
}

export const getLayerConfig = (serviceId: string, layerId: number, layers) => {
    if (serviceId === null || layerId === null) {
        return;
    }

    const serviceConfig = getServiceConfig(serviceId, layers);
    if (!serviceConfig || !serviceConfig.searchLayers) {
        return;
    }

    const sameLayerConfigs = serviceConfig.searchLayers.filter((layerConfig) => layerConfig.id === layerId);

    return sameLayerConfigs.length ? sameLayerConfigs[0] : undefined;
};

export const getServiceConfig = (serviceId: string, layers) => {
    const sameIdServiceConfigs = layers.filter((layer) => layer.id === serviceId);

    return sameIdServiceConfigs.length ? sameIdServiceConfigs[0] : undefined;
};

export const getSearchGroupConfig = (config, groupId: string | number) => {
    if (!config.search || !config.search.groups || !config.search.groups.length) {
        return;
    }

    const sameSearchGroups = config.search.groups.filter((group) => group.id === groupId);

    return sameSearchGroups.length ? sameSearchGroups[0] : undefined;
};

export const getGroupSearchConfigs = (config, searchGroupId: ISingleRowList) => {
    const searchGroupConfig = getSearchGroupConfig(config, searchGroupId.value);

    if (!searchGroupConfig) {
        return null;
    }

    const searchConfigs = [];

    searchGroupConfig.services.forEach((searchService) => {
        const layerConfigs = [];

        searchService.layerIds.forEach((layerId) => {
            const layerConfig = getLayerConfig(searchService.serviceId, layerId, config.layers);
            layerConfig && layerConfigs.push(layerConfig);
        });

        if (layerConfigs.length) {
            searchConfigs.push({
                serviceId: searchService.serviceId,
                layerConfigs
            });
        }
    });

    return searchConfigs;
};

export const replaceFieldsWithValues = (feature: Feature, layerInfo, text?: string): string => {
    if (!text) {
        return;
    }
    const replacedWithValues = text.replace(/(\[(?:\[??[^\[]*?\]))/g, (matched: string) => {
        const fieldName = matched.replace("[", "").replace("]", "");
        const fieldInfo = layerInfo.fields.find((field) => field.name === fieldName);
        if (fieldInfo && fieldInfo.domain && fieldInfo.domain.type === "coded-value") {
            const codedValue = fieldInfo.domain.codedValues.find((cv) => cv.code === feature.get(fieldName));
            return codedValue && codedValue.name;
        }
        return feature.get(fieldName);
    });

    return replacedWithValues
        .replace(/undefined/g, "")
        .replace(/null/g, "")
        .replace(/{}/g, "");
};

export const sortFeatures = (features: Feature[]): Feature[] =>
    features.length === 1
        ? features
        : features.sort((a, b) => {
              const valueA = a.get("NAMELV");
              const valueB = b.get("NAMELV");

              return valueA === undefined && valueB === undefined
                  ? 0
                  : valueA === undefined
                  ? 1
                  : valueB === undefined
                  ? -1
                  : typeof valueA === "string" || valueA instanceof String
                  ? valueA.localeCompare(valueB, "lv", { sensitivity: "accent" })
                  : valueA < valueB
                  ? -1
                  : valueA > valueB
                  ? 1
                  : 0;
          });

export const extendFeatures = (features: Feature[], layerConfig: ILayerConfig): any[] => {
    const extendedFeatures = [];
    const layer = new GeoJSLayer(layerConfig, layerConfig.layerInfo, layerConfig.serviceId, layerConfig.id);
    const displayExpression = layer.getDisplayExpression();

    features.map(async (feature) => {
        const label = replaceFieldsWithValues(feature, layerConfig.layerInfo, displayExpression);
        try {
            const newFeature = new Feature();
            newFeature.setGeometry(feature.getGeometry());
            newFeature.setProperties(feature.getProperties());
            newFeature.setProperties({
                serviceId: layerConfig.serviceId,
                layerId: layerConfig.id,
                layerInfo: layerConfig.layerInfo,
                hiddenFieldNames: null,
                label
            });
            extendedFeatures.push(newFeature);
        } catch (error) {
            console.error(error);
            return;
        }
    });

    return extendedFeatures;
};

export const createFeatureGroup = (layerConfig: ILayerConfig, features: GeoJSFeature[], sortGroup?: boolean) =>
    ({
        features,
        id: layerConfig.id,
        label: layerConfig.label,
        sortGroup
    } as IFeatureGroup);
